import styled from '@emotion/styled';
import Button from '@ep/components/Button';
import Container from '@ep/components/Container';
import ContactUsModal from '@ep/components/Layout/Header/ContactUs/ContactUsModal';
import Link from '@ep/components/Link';
import Media from '@ep/components/Media';
import SocialShare from '@ep/components/SocialShare';
import {
  Body,
  H1,
  H2,
  H3,
  Label,
  Subhead2
  } from '@ep/templates/ProductPage';
import theme from '@ep/theme';
import { Gcms_Asset, Gcms_RichText } from '@ep/types/gatsbyTypes';
import getHtmlProp from '@ep/utils/getHtmlProp';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import React from 'react';
import typography from 'theme/typography';

export interface HeroVideoModuleProps {
  backgroundImage: Gcms_Asset;
  image: Gcms_Asset;
  label: string;
  heading?: string;
  subHeading?: string;
  subheadingRichText?: Gcms_RichText;
  textColor?: string;
  description?: string;
  buttonLabel?: string;
  buttonLink?: string;
  unstyledButtonLabel?: string;
  unstyledButtonLink?: string;
  video?: Gcms_Asset;
  videoPositionLeft?: boolean;
  videoTitle?: string;
  videoText?: string;
  socialShareUrl?: string;
  pageHasSectionMenu?: boolean;
}

const StyledContainer = styled(Container)`
  /*margin-top: 1rem;*/
  margin-bottom: 0rem;
  display: flex;
  justify-content: space-between;
  flex-direction: ${({ videoPositionLeft }) =>
    videoPositionLeft ? `row-reverse` : `row`};
  padding: ${({pageHasSectionMenu})=>
     pageHasSectionMenu ? `6rem 0 3.5rem 0 !important;` : `3.5rem 0;`
  }
  h1 {
    margin: 0rem;
  }
  ${theme.mediaQueries.mobileAndTablet} {
    margin-top: 2rem;
    flex-direction: column-reverse;
    padding: 3rem 0 2rem;
  }
`;

// const StyledH3 = styled(H3)`
//   color: ${theme.colors.darkBlue};
// `;

const LeftText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${(props) =>
    props.videoPositionLeft ? `padding-left: 2rem` : `padding-right: 2rem`};
  overflow: hidden;
  ${theme.mediaQueries.mobile} {
    ${(props) =>
      props.videoPositionLeft ? `padding-left: 0rem` : `padding-right: 1rem`};
    h1 {
      width: 22rem;
    }
  }
`;

const Subheading = styled.h4`
  font-family: ${typography.secondaryFont.light};
  font-weight: normal;
  white-space: pre-wrap;
  margin: 0.5rem 0;
`;

const CTAElements = styled.div<HeroVideoModuleProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
  a {
    color: ${({ textColor }) => (textColor ? `${textColor.hex}` : `white`)};
  }
`;

const RightImage = styled.div`
  flex: 1;
  aspect-ratio: 16 / 9;
  position: relative;
  margin-bottom: 1rem;
`;

const HeroImage = styled(Media)`
  .vidyard-lightbox-image {
    max-height: inherit;
  }
  img {
    max-height: 30rem;
    object-position: 0 0;
  }
  ${theme.mediaQueries.mobileAndTablet} {
    width: 100%;
  }
`;

const HeroDescription = styled.p`
  font-family: ${theme.typography.lightFont};
  line-height: 1.5;
  font-size: 1.375rem;
  ${theme.mediaQueries.mobileAndTablet} {
    font-size: 1.2rem;
  }
`;

const HeroLabel = styled.h1`
  font-family: ${theme.typography.boldFont};
  font-size: 1.25rem;
  font-weight: 700;
`;

const HeroCatch = styled.h3`
  font-family: ${theme.typography.semiBoldFont};
  font-size: 2.5rem;
  margin: 1.5rem 0 0;
  line-height: 1.2;
  white-space: pre-wrap;
`;

const HeroVideo = styled(Media)`
  width: 100%;
  top: 0;
  left: 0;
`;

const HeroDim = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  top: 0rem;
  z-index: 1;
  width: 100%;
  aspect-ratio: 16 / 9;
  position: absolute;
  top: 0;
  left: 0;
`;

const ImageItems = styled.div`
  position: relative;
  margin-top: -12rem;
  z-index: 1;
  width: 60%;
  margin-left: 2rem;
  ${theme.mediaQueries.mobile} {
    margin-top: -10rem;
    width: 80%;
    margin-bottom: 2rem;
    margin-left: 1rem;
  }
  ${theme.mediaQueries.tablet} {
    width: 100%;
    margin-bottom: 6rem;
  }
`;

const PlayButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3rem;
  width: 3rem;
  border-radius: 1.5rem;
  background-color: ${theme.colors.white};
  color: ${theme.colors.duskBlue};
  cursor: pointer;
`;

const StyledModal = styled(Modal)`
  width: 80%;
  margin: 0 auto;
  height: 100vh;
  display: flex;
  align-items: center;

  ${theme.mediaQueries.mobileAndTablet} {
    width: 100%;
  }
`;

const ModalContainer = styled.div`
  width: 100%;
  margin: 0;
`;

const HeroVideoModule = ({
  backgroundImage,
  image,
  label,
  heading,
  subHeading,
  subheadingRichText,
  textColor,
  description,
  buttonLabel,
  buttonLink,
  unstyledButtonLabel,
  unstyledButtonLink,
  video,
  videoPositionLeft,
  videoTitle,
  videoText,
  socialShareUrl,
  pageHasSectionMenu,
}: HeroVideoModuleProps) => {
  const [openLightBox, setOpenLightBox] = React.useState(false);

  // const handlePlayButtonClick = () => {
  //   dataLayer.push({ event: 'Page Interaction – user clicked on hero video' });
  // };
  // const handleButtonClick = (buttonLabel: string) => {
  //   dataLayer.push({
  //     event: 'Page Interaction – user clicked on hero button - ' + buttonLabel,
  //   });
  // };
  // const handleLinkClick = (linkLabel: string) => {
  //   dataLayer.push({
  //     event: 'Page Interaction – user clicked on hero link - ' + linkLabel,
  //   });
  // };

  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    // dataLayer.push({
    //   event: 'Page Interaction – user clicked on hero button - ' + buttonLabel,
    // });
    const contactForm = document.getElementById('contact-form');

    if (contactForm) {
      contactForm.scrollIntoView({ block: 'center' });
    } else {
      setOpen(true);
    }
  };

  const CloseModal = styled.div`
    position: absolute;
    right: 0rem;
    top: 0rem;
    z-index: 10;
    ${theme.mediaQueries.tablet} {
      top: 12rem;
    }
    ${theme.mediaQueries.mobile} {
      top: 16rem;
    }
  `;

  const handleClose = () => {
    setOpen(false);
  };
  
  return (
    <>
      <StyledContainer
        background={backgroundImage}
        videoPositionLeft={videoPositionLeft}
        style={{ color: textColor ? textColor.hex : theme.colors.duskBlue }}
        pageHasSectionMenu={pageHasSectionMenu}
      >
        {socialShareUrl && (
          <SocialShare
            pathname={socialShareUrl}
            alignRight={!videoPositionLeft}
            darkBackground={backgroundImage}
            instanceAt={'HeroVideoModule'}
          />
        )}
        <LeftText videoPositionLeft={videoPositionLeft}>
          {label && <HeroLabel>{label}</HeroLabel>}
          {heading && <HeroCatch>{heading}</HeroCatch>}
          {subHeading && <Subheading>{subHeading}</Subheading>}
          {subheadingRichText && (
            <Subheading {...getHtmlProp(subheadingRichText.html)} />
          )}
          {description && <HeroDescription>{description}</HeroDescription>}
          {(buttonLabel || unstyledButtonLabel) && (
            <CTAElements>
              {buttonLabel &&
                (buttonLink === '#contact-us' ? (
                  <>
                    <Button label={buttonLabel} onClick={() => handleOpen()} />
                    <ContactUsModal open={open} onClose={() => handleClose()} />
                  </>
                ) : (
                  <Button
                    label={buttonLabel}
                    link={buttonLink}
                    // onClick={() => handleButtonClick(buttonLabel)} 
                  />
                ))}
              {unstyledButtonLabel && (
                <Link
                  to={unstyledButtonLink}
                  style={{ marginTop: '1rem' }}
                  // onClick={() => handleLinkClick(unstyledButtonLabel)} 
                >
                  {unstyledButtonLabel}
                </Link>
              )}
            </CTAElements>
          )}
        </LeftText>
        <RightImage>
          <HeroImage media={image} aspectRatio={16 / 9} cover />
          {video && <HeroDim />}
          {(video || videoTitle || videoText) && (
            <ImageItems>
              {video && (
                <PlayButton
                  onClick={() => {
                    // handlePlayButtonClick(); 
                    setOpenLightBox(true);
                  }}
                >
                  <PlayArrowIcon style={{ fontSize: '2rem' }} />
                </PlayButton>
              )}
              {videoTitle && (
                <H3
                  style={{ margin: '0.5rem 0', color: `${theme.colors.white}` }}
                >
                  {videoTitle}
                </H3>
              )}
              {videoText && (
                <Body style={{ color: `${theme.colors.white}` }}>
                  {videoText}
                </Body>
              )}
            </ImageItems>
          )}
        </RightImage>
        <StyledModal open={openLightBox} onClose={() => setOpenLightBox(false)}>
          <ModalContainer>
            <CloseModal>
              <IconButton
                aria-label="close"
                onClick={() => setOpenLightBox(false)}
              >
                <CloseIcon htmlColor={theme.colors.white} />
              </IconButton>
            </CloseModal>
            <HeroVideo media={video} />
          </ModalContainer>
        </StyledModal>
      </StyledContainer>
    </>
  );
};

export default HeroVideoModule;
